import React, { useState, useEffect } from 'react';
import { useNavigate } from 'react-router-dom';
import { ArrowRight, CheckCircle2, Sparkles, TrendingUp, Users, Calendar } from 'lucide-react';
import { Button } from '../components/ui/button';
import { Input } from '../components/ui/input';
import { Label } from '../components/ui/label';
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from '../components/ui/select';
import { api } from '../App';
import { toast } from 'sonner';
import { trackSignup } from '../utils/analytics';

const LandingPage = ({ onSignup, user }) => {
  const navigate = useNavigate();
  const [name, setName] = useState('');
  const [email, setEmail] = useState('');
  const [weeklyCommitment, setWeeklyCommitment] = useState('120');
  const [acceptedTerms, setAcceptedTerms] = useState(false);
  const [loading, setLoading] = useState(false);

  useEffect(() => {
    // Only redirect existing users to dashboard on page load
    // New users will be redirected via the signup handler
    if (user && !sessionStorage.getItem('kodo_just_signed_up')) {
      navigate('/dashboard');
    }
    // Clear the flag after checking
    sessionStorage.removeItem('kodo_just_signed_up');
  }, [user, navigate]);

  const handleSubmit = async (e) => {
    e.preventDefault();
    
    if (!acceptedTerms) {
      toast.error('Please accept the terms and conditions to continue.');
      return;
    }
    
    setLoading(true);

    try {
      const response = await api.post('/users/signup', { 
        name, 
        email, 
        weekly_commitment: parseInt(weeklyCommitment) 
      });
      
      // Track signup with commitment level
      const commitmentHours = parseInt(weeklyCommitment) / 60;
      trackSignup(commitmentHours);
      
      // Check if user needs onboarding (hasn't started any activities)
      const needsOnboarding = response.data.needs_onboarding === true;
      
      // Only show toast for returning users who have started
      if (!needsOnboarding) {
        toast.success('Welcome back! Your journey continues.');
      }
      
      onSignup(response.data);
      
      // Users who haven't started go to welcome page, others go to dashboard
      if (needsOnboarding) {
        sessionStorage.setItem('kodo_just_signed_up', 'true');
        navigate('/welcome');
      } else {
        navigate('/dashboard');
      }
    } catch (error) {
      toast.error('Something went wrong. Please try again.');
    } finally {
      setLoading(false);
    }
  };

  return (
    <div className="min-h-screen">
      {/* Hero Section */}
      <div className="hero-gradient min-h-screen flex items-center">
        <div className="container mx-auto px-6 md:px-12 lg:px-24 py-10">
          <div className="grid grid-cols-1 lg:grid-cols-2 gap-12 items-center">
            {/* Left Column */}
            <div className="space-y-16 animate-fade-in">
              {/* Logo and Program Label */}
              <div className="space-y-2 -mt-8">
                <img 
                  src="/kodo-logo.png" 
                  alt="Kodo" 
                  className="h-32 md:h-40 w-auto block"
                />
                <span className="inline-block text-base md:text-lg font-medium tracking-wide uppercase text-primary bg-blue-50 px-4 py-2 rounded-full">
                  6-Week Career Transformation Programme
                </span>
              </div>
              
              <h2 className="font-heading text-5xl md:text-7xl font-bold tracking-tight leading-tight text-slate-900">
                Feeling stuck in your career
              </h2>
              
              <p className="text-lg md:text-xl leading-relaxed text-slate-600">
                Gain clarity on your next career move. Our 6-week programme helps you discover what you really want and gives you a clear plan to get there.
              </p>

              <div className="space-y-4">
                <div className="flex items-start gap-3">
                  <CheckCircle2 className="w-6 h-6 text-primary flex-shrink-0 mt-1" />
                  <div>
                    <p className="font-semibold text-slate-900">Structured 6-week journey</p>
                    <p className="text-slate-600">Weekly activities designed by career experts</p>
                  </div>
                </div>
                <div className="flex items-start gap-3">
                  <CheckCircle2 className="w-6 h-6 text-primary flex-shrink-0 mt-1" />
                  <div>
                    <p className="font-semibold text-slate-900">AI-powered insights</p>
                    <p className="text-slate-600">Get personalized guidance based on your responses</p>
                  </div>
                </div>
                <div className="flex items-start gap-3">
                  <CheckCircle2 className="w-6 h-6 text-primary flex-shrink-0 mt-1" />
                  <div>
                    <p className="font-semibold text-slate-900">Actionable next steps</p>
                    <p className="text-slate-600">Clear direction for your career transition</p>
                  </div>
                </div>
                {/* Removed: Developed by experts */}
                <div className="flex items-start gap-3">
                  <CheckCircle2 className="w-6 h-6 text-primary flex-shrink-0 mt-1" />
                  <div>
                    <p className="font-bold text-slate-900">Free for limited time</p>
                    <p className="text-slate-600">Limited time free access to the programme!!</p>
                  </div>
                </div>
              </div>
            </div>

            {/* Right Column - Signup Form */}
            <div className="animate-slide-up w-full">
              <div className="bg-white rounded-2xl p-6 md:p-8 shadow-[0_8px_30px_rgb(0,0,0,0.08)] border border-slate-100">
                <div className="text-center mb-6">
                  <h2 className="font-heading text-3xl font-bold text-slate-900 mb-2">
                    Start Your Journey
                  </h2>
                  <p className="text-slate-600">
                    Join hundreds of professionals finding their next step
                  </p>
                </div>

                <form onSubmit={handleSubmit} className="space-y-6" data-testid="signup-form">
                  <div className="space-y-2">
                    <Label htmlFor="name" className="text-slate-700 font-medium">
                      Your Name
                    </Label>
                    <Input
                      id="name"
                      data-testid="signup-name-input"
                      type="text"
                      placeholder="Alex Johnson"
                      value={name}
                      onChange={(e) => setName(e.target.value)}
                      required
                      className="h-12 rounded-lg border-slate-200 bg-slate-50/50 focus:bg-white focus:ring-2 focus:ring-blue-100 focus:border-primary"
                    />
                  </div>

                  <div className="space-y-2">
                    <Label htmlFor="email" className="text-slate-700 font-medium">
                      Email Address
                    </Label>
                    <Input
                      id="email"
                      data-testid="signup-email-input"
                      type="email"
                      placeholder="alex@example.com"
                      value={email}
                      onChange={(e) => setEmail(e.target.value)}
                      required
                      className="h-12 rounded-lg border-slate-200 bg-slate-50/50 focus:bg-white focus:ring-2 focus:ring-blue-100 focus:border-primary"
                    />
                  </div>

                  <div className="space-y-2">
                    <Label htmlFor="commitment" className="text-slate-700 font-medium">
                      Weekly commitment
                    </Label>
                    <Select value={weeklyCommitment} onValueChange={setWeeklyCommitment}>
                      <SelectTrigger 
                        className="h-12 rounded-lg border-slate-200 bg-slate-50/50 focus:bg-white focus:ring-2 focus:ring-blue-100 focus:border-primary"
                        data-testid="signup-commitment-select"
                      >
                        <SelectValue placeholder="Select your weekly commitment" />
                      </SelectTrigger>
                      <SelectContent>
                        <SelectItem value="60" data-testid="commitment-60">1 hr</SelectItem>
                        <SelectItem value="120" data-testid="commitment-120">2 hrs (most popular)</SelectItem>
                        <SelectItem value="180" data-testid="commitment-180">3 hrs</SelectItem>
                      </SelectContent>
                    </Select>
                  </div>

                  {/* Terms Checkbox */}
                  <div className="flex items-start gap-3">
                    <input
                      type="checkbox"
                      id="terms"
                      checked={acceptedTerms}
                      onChange={(e) => setAcceptedTerms(e.target.checked)}
                      className="mt-1 h-4 w-4 rounded border-slate-300 text-primary focus:ring-primary cursor-pointer"
                      data-testid="terms-checkbox"
                    />
                    <label htmlFor="terms" className="text-sm text-slate-600 cursor-pointer">
                      I accept the{' '}
                      <button
                        type="button"
                        onClick={() => navigate('/terms')}
                        className="text-primary font-medium hover:underline"
                      >
                        terms and conditions
                      </button>
                    </label>
                  </div>

                  <Button
                    type="submit"
                    data-testid="signup-submit-button"
                    disabled={loading || !acceptedTerms}
                    className="w-full h-12 rounded-full bg-primary text-white font-semibold shadow-lg hover:shadow-xl btn-hover disabled:opacity-50 disabled:cursor-not-allowed"
                  >
                    {loading ? 'Signing Up...' : 'Get Started Now'}
                    {!loading && <ArrowRight className="ml-2 w-5 h-5" />}
                  </Button>

                  <p className="text-xs text-center text-slate-500">
                    No credit card required. Start your transformation today.
                  </p>
                </form>
              </div>
            </div>
          </div>
        </div>
      </div>

      {/* How It Works Section */}
      <div className="py-20 md:py-32 bg-white">
        <div className="container mx-auto px-6 md:px-12 lg:px-24">
          <div className="text-center mb-16">
            <h2 className="font-heading text-4xl md:text-5xl font-bold text-slate-900 mb-4">
              How It Works
            </h2>
            <p className="text-lg text-slate-600 max-w-2xl mx-auto">
              A simple, structured approach to finding clarity in your career
            </p>
          </div>

          <div className="grid grid-cols-1 md:grid-cols-3 gap-8">
            <div className="bg-white rounded-2xl p-8 border border-slate-100 shadow-[0_4px_20px_-2px_rgba(0,0,0,0.05)] week-card">
              <div className="w-12 h-12 bg-blue-100 rounded-full flex items-center justify-center mb-6">
                <Calendar className="w-6 h-6 text-primary" />
              </div>
              <h3 className="font-heading text-2xl font-semibold text-slate-900 mb-3">
                Weekly Activities
              </h3>
              <p className="text-slate-600 leading-relaxed">
                Receive thoughtfully designed activities each week via email. Each week focusses on a different area.
              </p>
            </div>

            <div className="bg-white rounded-2xl p-8 border border-slate-100 shadow-[0_4px_20px_-2px_rgba(0,0,0,0.05)] week-card">
              <div className="w-12 h-12 bg-orange-100 rounded-full flex items-center justify-center mb-6">
                <Sparkles className="w-6 h-6 text-secondary" />
              </div>
              <h3 className="font-heading text-2xl font-semibold text-slate-900 mb-3">
                AI Analysis
              </h3>
              <p className="text-slate-600 leading-relaxed">
                Our AI analyzes your responses to understand your situation, values, and goals. No generic advice.
              </p>
            </div>

            <div className="bg-white rounded-2xl p-8 border border-slate-100 shadow-[0_4px_20px_-2px_rgba(0,0,0,0.05)] week-card">
              <div className="w-12 h-12 bg-cyan-100 rounded-full flex items-center justify-center mb-6">
                <TrendingUp className="w-6 h-6 text-accent-cyan" />
              </div>
              <h3 className="font-heading text-2xl font-semibold text-slate-900 mb-3">
                Actionable Insights
              </h3>
              <p className="text-slate-600 leading-relaxed">
                Get personalized insights and concrete next steps. Build momentum toward your ideal career path.
              </p>
            </div>
          </div>
        </div>
      </div>

      {/* CTA Section */}
      <div className="py-20 bg-gradient-to-br from-blue-600 to-blue-700">
        <div className="container mx-auto px-6 md:px-12 lg:px-24 text-center">
          <h2 className="font-heading text-4xl md:text-5xl font-bold text-white mb-6">
            Ready to Find Your Next Chapter?
          </h2>
          <p className="text-xl text-blue-100 mb-8 max-w-2xl mx-auto">
            Join professionals who've already started their transformation journey
          </p>
          <Button
            onClick={() => window.scrollTo({ top: 0, behavior: 'smooth' })}
            className="h-14 px-10 rounded-full bg-white text-primary font-semibold shadow-lg hover:shadow-xl hover:bg-blue-50 hover:scale-105 transition-all text-lg"
            data-testid="cta-get-started-button"
          >
            Get Started Now
            <ArrowRight className="ml-2 w-5 h-5" />
          </Button>
        </div>
      </div>
      {/* Footer */}
      <div className="py-8 bg-slate-50 border-t border-slate-100">
        <div className="container mx-auto px-6 md:px-12 lg:px-24">
          <div className="flex flex-col md:flex-row items-center justify-between gap-4">
            <div className="flex items-center gap-2">
              <img 
                src="/kodo-logo.png" 
                alt="Kodo" 
                className="h-8 w-auto"
              />
            </div>
            <div className="flex items-center gap-6 text-sm text-slate-600">
              <button 
                onClick={() => navigate('/about')}
                className="hover:text-primary transition-colors"
              >
                About Us
              </button>
              <button 
                onClick={() => navigate('/terms')}
                className="hover:text-primary transition-colors"
              >
                Terms & Conditions
              </button>
              <a 
                href="mailto:hello@getkodo.co.uk"
                className="hover:text-primary transition-colors"
              >
                Contact
              </a>
            </div>
          </div>
          {/* Company registration */}
          <div className="mt-6 pt-4 border-t border-slate-200 text-center">
            <p className="text-xs text-slate-400">
              © {new Date().getFullYear()} Colourful Clay Limited. Company No. 11799231
            </p>
          </div>
        </div>
      </div>
    </div>
  );
};

export default LandingPage;